import React, { useState, useRef } from 'react';
import { RefreshCw, Upload, X } from 'lucide-react';

export function ImageSlot({ value, index, onChange, onUpload }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUrl, setShowUrl] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const inputRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowed = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
    if (!file.type.startsWith('image/') && !allowed.includes(ext)) {
      setUploadError('Only image files are supported (JPG, PNG, WebP, HEIC).');
      return;
    }
    if (ext && !allowed.includes(ext)) {
      setUploadError(`"${ext}" files are not supported. Save as JPG or PNG first.`);
      return;
    }
    setUploadError('');
    setUploading(true);
    setProgress(0);
    try {
      const url = await onUpload(file, setProgress);
      onChange(url);
      setShowUrl(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Upload failed. Try a smaller image or paste a URL instead.';
      setUploadError(msg);
    }
    setUploading(false);
    setProgress(0);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="flex items-start gap-2">
      <span className="text-[10px] text-[#AAAAAA] font-work w-14 flex-shrink-0 pt-3">
        Image {index + 1}
      </span>
      <div className="flex-1">
        {value && !uploading ? (
          <div>
            <div className="relative group">
              <img
                src={value} alt=""
                className="w-full h-32 object-cover border border-[#E5E5E5]"
                onError={e => { e.target.style.opacity = '0.3'; }}
              />
              <button
                type="button" onClick={() => { onChange(''); setShowUrl(false); }}
                className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-image-${index}`}>
                <X size={11} />
              </button>
            </div>
            <div className="flex items-center gap-3 mt-1.5">
              <button type="button" onClick={() => inputRef.current.click()}
                className="text-[10px] text-[#D4AF37] font-work hover:text-[#0A0A0A] transition-colors">
                Replace
              </button>
              <span className="text-[#DDDDDD] text-xs">|</span>
              <button type="button" onClick={() => setShowUrl(!showUrl)}
                className="text-[10px] text-[#AAAAAA] font-work hover:text-[#D4AF37] transition-colors">
                Paste URL
              </button>
            </div>
            {showUrl && (
              <input value={value} onChange={e => onChange(e.target.value)}
                placeholder="https://..."
                className="w-full border border-[#E5E5E5] px-3 py-1.5 text-xs font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]"
                data-testid={`image-url-${index}`} />
            )}
          </div>
        ) : (
          <div>
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => !uploading && inputRef.current.click()}
              className={`border-2 border-dashed transition-colors cursor-pointer p-5 flex flex-col items-center gap-2 ${dragOver ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#E5E5E5] hover:border-[#D4AF37]'} ${uploading ? 'cursor-default pointer-events-none' : ''}`}
              data-testid={`upload-zone-${index}`}>
              {uploading ? (
                <>
                  <RefreshCw size={18} className="text-[#D4AF37] animate-spin" />
                  <div className="w-full bg-[#E5E5E5] h-1 rounded-full overflow-hidden">
                    <div className="bg-[#D4AF37] h-full transition-all duration-200" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-[10px] text-[#D4AF37] font-work">{progress}% uploading...</p>
                </>
              ) : (
                <>
                  <Upload size={18} className="text-[#AAAAAA]" />
                  <p className="text-xs text-[#555555] font-work">Click or drag image here</p>
                  <p className="text-[10px] text-[#AAAAAA] font-work">JPG, PNG, WebP</p>
                </>
              )}
            </div>
            <button type="button" onClick={e => { e.stopPropagation(); setShowUrl(!showUrl); }}
              className="text-[10px] text-[#AAAAAA] font-work hover:text-[#D4AF37] transition-colors mt-1.5">
              {showUrl ? 'Hide URL input' : 'Or paste an image URL'}
            </button>
            {showUrl && (
              <input value={value} onChange={e => onChange(e.target.value)}
                placeholder="https://..."
                className="w-full border border-[#E5E5E5] px-3 py-1.5 text-xs font-work mt-1 focus:outline-none focus:border-[#D4AF37]"
                data-testid={`image-url-${index}`} />
            )}
          </div>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
          onChange={e => handleFile(e.target.files[0])} />
      </div>
      {uploadError && (
        <p className="text-[10px] text-red-500 font-work mt-1 flex items-center gap-1" data-testid={`upload-error-${index}`}>
          <X size={10} /> {uploadError}
        </p>
      )}
    </div>
  );
}
